import useDocumentTitle from "@/app/hooks/documentTitleHook"
import { useAppSelector } from "@/app/hooks/reduxHooks"
import { buildSearchUrl } from "@/app/utils/searchUtils"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent } from "@/components/ui/card"
import ContactCard from "@/components/ui/contactcard"
import ContactInformation from "@/components/ui/contactinformation"
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog"
import EditColumn from "@/components/ui/modals/editcolumn"
import type { ColumnOption } from "@/features/searchTable/slice/columnsSlice"
import { openColumnsModal } from "@/features/searchTable/slice/columnsSlice"
import { useAppDispatch } from "@/app/hooks/reduxHooks"
import ExportLeads from "@/components/ui/modals/exportleads"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { HoverCard } from "@/components/ui/hovercard"
import { DataTableColumn } from "@/interface/searchTable/components"
import { ContactAttributes, SearchApiResponse } from "@/interface/searchTable/search"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Eye, Mail, Phone, User2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import DownloadIcon from "../../static/media/icons/download-icon.svg?react"
import { selectSelectedItems } from "../filters/slice/filterSlice"
import { DataTable } from "./baseDataTable"
import { useSearchContactsQuery } from "./slice/apiSlice"
import { openContactInfoForContact } from "./slice/contactInfoSlice"

export function ContactsTable() {
  const dispatch = useAppDispatch()
  const columnsConfigFromStore = useAppSelector((s) => s.columns.configs.contact)
  useDocumentTitle("Search People")
  const selectedFilters = useAppSelector(selectSelectedItems)
  const [pagination, setPagination] = useState({
    page: 1,
    count: 20,
    total: 0,
    lastPage: 1
  })
  const [queryValue, setQueryValue] = useState("")
  const [sortSelected, setSortSelected] = useState<string[]>([])
  const [infoContact, setInfoContact] = useState<ContactAttributes | null>(null)
  const [isExportOpen, setIsExportOpen] = useState(false)

  // NEW: State for checkbox selection
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage
    }))
  }

  // NEW: Handle individual contact selection
  const handleContactSelect = useCallback((contactId: string) => {
    setSelectedContacts((prev) => (prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]))
  }, [])

  // NEW: Handle select all contacts
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allContactIds = data?.data?.map((contact) => contact.id || "") || []
      setSelectedContacts(allContactIds.filter((id) => id !== ""))
    } else {
      setSelectedContacts([])
    }
  }

  const transformedFilters = useMemo(() => {
    const filterEntries = Object.entries(selectedFilters)
      .filter(([, values]) => values && values.length > 0)
      .map(([filterType, filterValues]) => ({
        type: filterType,
        values: filterValues.map((value) => ({
          id: value.id,
          text: value.name,
          selectionType: value.type === "include" ? "INCLUDED" : "EXCLUDED"
        }))
      }))
    return filterEntries
  }, [selectedFilters])

  const queryParams = useMemo(
    () => ({
      ...(queryValue && { searchTerm: queryValue }),
      ...(transformedFilters.length > 0 && { filters: transformedFilters })
    }),
    [queryValue, transformedFilters]
  )

  const searchParams = useMemo(
    () => ({
      page: pagination.page,
      count: pagination.count,
      ...(sortSelected.length && { sort: sortSelected.join(",") })
    }),
    [pagination.page, pagination.count, sortSelected]
  )

  const searchUrl = useMemo(() => buildSearchUrl(searchParams, queryParams), [searchParams, queryParams])

  const { data, isLoading, isFetching } = useSearchContactsQuery({ type: "contact", buildParams: searchUrl }, { refetchOnMountOrArgChange: true })

  const sortableFields = ["full_name", "website", "title", "linkedin_url", "company"]

  // Enhanced columns with better styling and icons
  const baseColumns: DataTableColumn<ContactAttributes>[] = [
    {
      title: "Contact Name",
      field: "full_name",
      width: "w-1/3",
      render: (value, row) => (
        <TooltipProvider>
          <div className="flex items-center gap-3">
            <div>
              {value ? <div className="text-sm font-normal">{value}</div> : <span className="text-sm text-muted-foreground">Not Available</span>}
              {!!row.email && <div className="text-xs text-muted-foreground">{row.email}</div>}
            </div>
            {/* <Tooltip>
              <TooltipTrigger asChild>
                <a href={row.linkedin_url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center">
                  <LinkedinIcon className="size-4" />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p>{row.linkedin_url?.replace("http://www.", "")}</p>
              </TooltipContent>
            </Tooltip> */}
          </div>
        </TooltipProvider>
      )
    },

    {
      title: "Company",
      field: "company",
      width: "w-1/4",
      render: (value) => (
        <div className="flex items-center gap-2">
          {/* <Building2 className="size-4 shrink-0 text-muted-foreground" /> */}
          <Avatar className="flex size-6 shrink-0 items-center justify-center bg-primary/10">
            <User2 className="size-4 shrink-0 text-primary" />
          </Avatar>
          <div>{value || <span className="text-sm text-muted-foreground">Not Available</span>}</div>
        </div>
      )
    },
    {
      title: "Job Profile",
      field: "title",
      width: "w-1/4",
      render: (value) => <div className="flex items-center gap-2">{value || "N/A"}</div>
    },
    // {
    //   title: "Website",
    //   field: "website",
    //   width: "w-1/5",
    //   render: (value) => (
    //     <a
    //       href={value ? `https://${value}` : "#"}
    //       target="_blank"
    //       rel="noopener noreferrer"
    //       className="flex items-center gap-2 text-primary hover:underline"
    //     >
    //       <Globe className="size-4 shrink-0" />
    //       <span>{value || <span className="text-sm text-muted-foreground">Not Available</span>}</span>
    //     </a>
    //   )
    // }
    // {
    //   title: "Contact",
    //   field: "contact" as keyof ContactAttributes,
    //   width: "w-1/4",
    //   render: () => (
    //     <div className="flex items-center gap-1">
    //       <Button className="rounded-lg border bg-transparent px-1.5 py-1 hover:bg-transparent">
    //         <Phone className="size-3 text-[#5C5C5C]" />
    //       </Button>
    //       <Button className="rounded-lg border bg-transparent px-1.5 py-1 hover:bg-transparent">
    //         <Mail className="size-3 text-[#5C5C5C]" />
    //       </Button>
    //     </div>
    //   )
    // },
    {
      title: "Contact",
      field: "contact" as keyof ContactAttributes,
      width: "w-1/4",
      render: (_: unknown, row: ContactAttributes) => (
        <HoverCard
          className=""
          contentClassName="z-50 rounded-none bg-white p-0 shadow-none border-none translate-x-0 mt-0 "
          trigger={
            <div className="flex items-center gap-1">
              <Button className="rounded-lg border bg-transparent px-1.5 py-1 hover:bg-transparent">
                <Phone className="size-3 text-[#5C5C5C]" />
                <span className="rounded-full bg-[#EBEBEB] px-2 py-0.5 text-[11px] font-medium text-gray-950">1</span>
              </Button>
              <Button className="rounded-lg border bg-transparent px-1.5 py-1 hover:bg-transparent">
                <Mail className="size-3 text-[#5C5C5C]" />
                <span className="rounded-full bg-[#EBEBEB] px-2 py-0.5 text-[11px] font-medium text-gray-950">2</span>
              </Button>
            </div>
          }
          content={<ContactCard contact={row} secondaryPhone="+1983848484" />}
          side="left"
        />
      )
    },

    {
      title: "Actions",
      field: "actions" as keyof ContactAttributes,
      width: "w-1/5",
      render: (_: unknown, row: ContactAttributes) => (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Button size="sm" className="h-8 gap-2 bg-[#335CFF] text-xs hover:bg-[#335CFF]" onClick={() => setInfoContact(row)}>
                <Eye className="size-3" />
                Reveal info
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reveal contact will consume 5 credits</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }
  ]

  const contactColumnsConfig: ColumnOption[] = columnsConfigFromStore

  const visibleOrderedContactColumns: DataTableColumn<ContactAttributes>[] = contactColumnsConfig
    .filter((c) => c.visible)
    .map((cfg) => (baseColumns || []).find((bc) => (bc?.field as string) === cfg.field))
    .filter((c): c is DataTableColumn<ContactAttributes> => Boolean(c))

  useEffect(() => {
    if (data?.meta) {
      setPagination((prev) => ({
        ...prev,
        total: data.meta.total || 0,
        lastPage: data.meta.last_page || 1
      }))
    }
  }, [data?.meta])

  // NEW: Clear selection when data changes (page change, search, etc.)
  useEffect(() => {
    setSelectedContacts([])
  }, [pagination.page, queryValue, transformedFilters])

  return (
    // <Card className="h-full border-gray-200 bg-white backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950">
    <CardContent className="h-full p-0">
      <div className="flex h-full flex-col">
        {/* {transformedFilters.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {transformedFilters.map((filter) =>
              filter.values.map((value) => (
                <Badge key={value.id} variant={value.selectionType === "INCLUDED" ? "default" : "destructive"} className="text-xs">
                  {value.text}
                </Badge>
              ))
            )}
          </div>
        )} */}

        {/* NEW: Show selected count */}
        {selectedContacts.length > 0 && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm font-medium text-[#262626]">{selectedContacts.length} Selected</span>
            <Button
              variant="default"
              className="h-[28px] bg-[#476CFF19] px-1.5 py-1 text-sm font-medium text-[#335CFF] hover:bg-[#476CFF19] hover:text-[#335CFF]"
              onClick={() => setIsExportOpen(true)}
            >
              <DownloadIcon className="size-5 text-[#335CFF]" />
              Export as CSV
            </Button>
          </div>
        )}

        <div className="flex-1">
          <DataTable<ContactAttributes>
            columns={visibleOrderedContactColumns}
            data={(data as SearchApiResponse<ContactAttributes>)?.data || []}
            loading={isLoading}
            fetching={isFetching}
            sortableFields={sortableFields}
            onSort={setSortSelected}
            sortSelected={sortSelected}
            searchPlaceholder="Search contacts..."
            onSearch={setQueryValue}
            searchValue={queryValue}
            pagination={pagination}
            onPageChange={handlePageChange}
            showCheckbox={true}
            selectedItems={selectedContacts}
            onItemSelect={handleContactSelect}
            onSelectAll={handleSelectAll}
            onOpenEditColumns={() => dispatch(openColumnsModal("contact"))}
            onRowClick={(row) => {
              dispatch(openContactInfoForContact(row as ContactAttributes))
            }}
          />
        </div>
      </div>

      <Dialog open={!!infoContact} onOpenChange={(open) => !open && setInfoContact(null)}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed right-0 top-0 z-50 h-full w-[424px] border-l border-stone-200 bg-white shadow-xl focus:outline-none">
            <ContactInformation contact={infoContact} onClose={() => setInfoContact(null)} company={null} />
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
      <ExportLeads
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        selectedCount={selectedContacts.length}
        totalAvailable={pagination.total}
      />

      <EditColumn />
    </CardContent>
    // </Card>
  )
}

export default ContactsTable
